import dayjs from "dayjs";
import i18n from "features/parent/i18n";
import { FeedbackHistoryItem, FeedbackHistoryType, ContactType } from "features/parent/types";

interface FeedbackSummaryCardProps {
  item: FeedbackHistoryItem;
  onClick: () => void;
}

// 星評価を表示（コンパクト版）
const StarRatingCompact = ({ rating }: { rating: number }) => {
  return (
    <div className="flex-row-el items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`text-[21px] ${star <= rating ? "text-yellow-500" : "text-gray-300"}`}
        >
          ★
        </span>
      ))}
    </div>
  );
};

// 種別バッジを表示
const TypeBadge = ({ type }: { type: FeedbackHistoryType }) => {
  const config = {
    [FeedbackHistoryType.RATING]: {
      icon: "star",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_RATING"),
      color: "bg-yellow-100 text-yellow-700",
    },
    [FeedbackHistoryType.FEEDBACK]: {
      icon: "feedback",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_FEEDBACK"),
      color: "bg-blue-50 text-blue-700",
    },
    [FeedbackHistoryType.CONTACT]: {
      icon: "mail",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_CONTACT"),
      color: "bg-green-50 text-green-700",
    },
  };

  const { icon, label, color } = config[type];

  return (
    <div
      className={`flex-row-el items-center gap-1 px-2 py-0.5 rounded-[8px] text-[18px] ${color}`}
    >
      <span className="material-symbols-outlined text-[21px]">{icon}</span>
      {label}
    </div>
  );
};

// 問い合わせタイプを表示
const ContactTypeBadge = ({ contactType }: { contactType: ContactType }) => {
  const labels = {
    [ContactType.HOW_TO]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_HOW_TO"),
    [ContactType.BUG]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_BUG"),
    [ContactType.REQUEST]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_REQUEST"),
    [ContactType.OTHER]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_OTHER"),
  };

  return <span className="text-[18px] text-gray2-100">{labels[contactType]}</span>;
};

// complaint_type → i18nキーのマッピング
const COMPLAINT_TYPE_KEYS: Record<string, string> = {
  NONE: "MSG_REVIEW_FEEDBACK_TYPE_NONE",
  APP_HOW_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_APP_HOW_TO_USE",
  APP_HARD_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_APP_HARD_TO_USE",
  COURSE_TOO_DIFFICULT: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_DIFFICULT",
  COURSE_TOO_EASY: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_EASY",
  COURSE_TOO_MANY: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_MANY",
  COURSE_TOO_FEW: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_FEW",
  CREATOR_HARD_TO_CREATE: "MSG_REVIEW_FEEDBACK_TYPE_CREATOR_HARD_TO_CREATE",
  CREATOR_HARD_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_CREATOR_HARD_TO_USE",
};

// フィードバックタイプを表示（complaint_type）
const ComplaintTypeBadge = ({ complaintType }: { complaintType: string }) => {
  const types = complaintType.split(",").filter((t) => t.trim());
  const labels = types
    .map((type) => {
      const key = COMPLAINT_TYPE_KEYS[type.trim()];
      return key ? i18n.t(key).replace(/\n/g, "") : null;
    })
    .filter(Boolean);

  if (labels.length === 0) return null;

  return <span className="text-[18px] text-gray2-100">{labels.join(", ")}</span>;
};

// 日付フォーマット（日付のみ）
const formatDateShort = (dateString: string) => {
  return dayjs(dateString).format("YYYY-MM-DD");
};

// サマリーテキストを生成
const getSummaryText = (item: FeedbackHistoryItem): string => {
  if (item.type === FeedbackHistoryType.RATING && item.rating) {
    return i18n.t("MSG_PARENT_PAGE_FEEDBACK_RATING_SUMMARY", { rating: item.rating });
  }
  return item.detail || "";
};

export const FeedbackSummaryCard = ({ item, onClick }: FeedbackSummaryCardProps) => {
  const hasReply = !!item.reply;
  const summaryText = getSummaryText(item);
  // 評価（rating）の場合は返信ステータスを表示しない
  const showReplyStatus = item.type !== FeedbackHistoryType.RATING;

  return (
    <div
      className="bg-white rounded-[16px] py-4 px-5 cursor-pointer transition-all shadow-sm border border-outline hover:shadow-md"
      onClick={onClick}
    >
      {/* 1行目: 日付 + 返信ステータス（評価以外） */}
      <div className="flex-row-el items-center justify-between mb-2">
        <span className="text-[19px] text-gray2-100">{formatDateShort(item.created_at)}</span>
        {showReplyStatus &&
          (hasReply ? (
            <span className="flex-row-el items-center gap-1 text-[18px] text-green-100">
              <span className="material-symbols-outlined text-[21px]">check_circle</span>
              {i18n.t("MSG_PARENT_PAGE_FEEDBACK_REPLIED")}
            </span>
          ) : (
            <span className="flex-row-el items-center gap-1 text-[18px] text-gray2-100">
              <span className="material-symbols-outlined text-[21px]">schedule</span>
              {i18n.t("MSG_PARENT_PAGE_FEEDBACK_PENDING")}
            </span>
          ))}
      </div>

      {/* 2行目: タイプバッジ + 星評価 + 問い合わせタイプ + フィードバックタイプ */}
      <div className="flex-row-el items-center gap-2 mb-2 flex-wrap">
        <TypeBadge type={item.type} />
        {item.type === FeedbackHistoryType.RATING && item.rating && (
          <StarRatingCompact rating={item.rating} />
        )}
        {item.type === FeedbackHistoryType.CONTACT && item.contact_type && (
          <ContactTypeBadge contactType={item.contact_type} />
        )}
        {item.type === FeedbackHistoryType.FEEDBACK && item.complaint_type && (
          <ComplaintTypeBadge complaintType={item.complaint_type} />
        )}
      </div>

      {/* 3行目: サマリーテキスト（truncate） */}
      {summaryText && (
        <div className="text-[21px] text-textcolor-black leading-[1.5] truncate">{summaryText}</div>
      )}
    </div>
  );
};
